import { broadcastApiTransaction } from "boltz-swaps/client";
import {
    Explorer,
    type ExplorerUrl,
    SwapType,
    type Url,
} from "boltz-swaps/types";
import log from "loglevel";

import { chooseUrl, config } from "../config";
import {
    BTC,
    ETH,
    LBTC,
    RBTC,
    TBTC,
    type blockChainsAssets,
    refundableAssets,
} from "../consts/Assets";
import { formatError } from "./errors";
import { constructRequestOptions } from "./helper";
import type { ChainSwap, SubmarineSwap } from "./swapCreator";

export type UTXO = {
    txid: string;
    vout: number;
};

type SwapUTXO = {
    hex: string;
    id: string;
    timeoutBlockHeight?: number;
};

type MempoolFeeEstimation = Record<
    "fastestFee" | "halfHourFee" | "hourFee" | "economyFee" | "minimumFee",
    number
>;

export const blockTimeMinutes: Record<blockChainsAssets, number> = {
    [BTC]: 10,
    [LBTC]: 1,
    [RBTC]: 25 / 60,
    [ETH]: 12 / 60,
};

export const getNetworkName = (asset: string) => {
    switch (asset) {
        case BTC:
            return "Bitcoin";
        case LBTC:
            return "Liquid";
        case RBTC:
            return "Rootstock";
        case TBTC:
            return "Arbitrum";
        case ETH:
            return "Ethereum";
        default:
            return "";
    }
};

const handleResponseSuccess = async <T>(response: Response): Promise<T> => {
    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
        return (await response.json()) as T;
    }
    return (await response.text()) as T;
};

const handleResponseError = async (response: Response) => {
    const errorMessage = `HTTP ${response.status} from ${response.url}`;
    const body = (await response.text().catch(() => "")).trim();
    if (body === "") {
        throw new Error(errorMessage);
    }

    let details = body;
    try {
        details = formatError(JSON.parse(body));
    } catch {
        // Explorers answer rejected broadcasts with plain text
    }
    throw new Error(`${errorMessage}: ${details}`);
};

// Explorers relay the node's rejection reason of a broadcast in one of two
// formats:
//   mempool: sendrawtransaction RPC error: {"code":-26,"message":"non-final"}
//   esplora: sendrawtransaction RPC error -26: non-final
export const parseExplorerRejection = (message: string): string | undefined => {
    const trimmed = message.trim();

    const json = trimmed.match(/RPC error: (\{.*\})$/);
    if (json) {
        try {
            const parsed = JSON.parse(json[1]) as { message?: unknown };
            if (typeof parsed.message === "string") {
                return parsed.message;
            }
        } catch {
            // Fall through to the esplora format
        }
    }

    return trimmed.match(/RPC error -?\d+: (.+)$/)?.[1];
};

const getExplorerRejection = (error: unknown): string | undefined => {
    const cause = error instanceof Error ? error.cause : undefined;
    if (!(cause instanceof AggregateError)) {
        return undefined;
    }

    for (const explorerError of cause.errors) {
        if (!(explorerError instanceof Error)) {
            continue;
        }
        const rejection = parseExplorerRejection(explorerError.message);
        if (rejection !== undefined) {
            return rejection;
        }
    }

    return undefined;
};

/**
 * Sequentially fetches resources from block explorers APIs and returns the response
 */
const fetchBlockExplorer = async <T>(
    asset: string,
    endpoint: string,
    options: RequestInit = {},
): Promise<T> => {
    for (const url of config.assets?.[asset]?.blockExplorerApis ?? []) {
        const { opts, requestTimeout } = constructRequestOptions(options);

        try {
            const basePath = chooseUrl(url);

            const res = await fetch(`${basePath}${endpoint}`, opts);

            if (!res.ok) {
                try {
                    const body = await res.json();
                    log.error(
                        `block explorer fetch ${endpoint} for asset ${asset} failed`,
                        formatError(body),
                    );
                    continue;
                } catch {
                    // If parsing JSON fails, throw a generic error with status text
                    log.error(
                        `block explorer fetch ${endpoint} for asset ${asset} failed`,
                        res.statusText,
                    );
                    continue;
                }
            }

            return await handleResponseSuccess<T>(res);
        } catch (e) {
            log.error(
                `block explorer fetch ${endpoint} for asset ${asset} failed`,
                e,
            );
            continue;
        } finally {
            clearTimeout(requestTimeout);
        }
    }

    throw new Error(
        `all block explorer APIs failed for asset ${asset}, endpoint ${endpoint}`,
    );
};

/**
 * Fetches resources from multiple block explorer APIs in parallel and returns the first successful response
 */
const fetchBlockExplorerParallel = async <T>(
    asset: string,
    endpoint: string,
    options: RequestInit = {},
): Promise<T> => {
    const urls = config.assets?.[asset]?.blockExplorerApis ?? [];

    try {
        const parallelPromises = urls.map(async (url) => {
            const { opts, requestTimeout } = constructRequestOptions(options);
            try {
                const basePath = chooseUrl(url);

                const res = await fetch(`${basePath}${endpoint}`, opts);

                if (!res.ok) {
                    await handleResponseError(res);
                }

                return res;
            } finally {
                clearTimeout(requestTimeout);
            }
        });

        const response = await Promise.any(parallelPromises);

        return await handleResponseSuccess<T>(response);
    } catch (err) {
        if (err instanceof AggregateError) {
            err.errors.forEach((e, i) => {
                log.error(
                    `fetch to external explorer ${chooseUrl(urls[i])} failed: ${e}`,
                );
            });
        }
        throw new Error(`all external fetch attempts to ${endpoint} failed`, {
            cause: err,
        });
    }
};

const getAddressUTXOs = async (asset: string, address: string) => {
    return await fetchBlockExplorer<UTXO[]>(asset, `/address/${address}/utxo`);
};

const getRawTransaction = async (asset: string, txid: string) => {
    return await fetchBlockExplorer<string>(asset, `/tx/${txid}/hex`);
};

export const getBlockTipHeight = async (asset: string) => {
    const height = await fetchBlockExplorer<string>(
        asset,
        "/blocks/tip/height",
    );

    if (!Number.isFinite(Number(height))) {
        throw new Error(
            `invalid block tip height for asset ${asset}: ${height}`,
        );
    }

    return height;
};

export const getTransactionOutSpend = async (
    asset: string,
    txid: string,
    vout: number,
) => {
    return await fetchBlockExplorer<{ spent: boolean; txid?: string }>(
        asset,
        `/tx/${txid}/outspend/${vout}`,
    );
};

export const broadcastToExplorer = async (
    asset: string,
    txHex: string,
): Promise<{ id: string }> => {
    const txId = await fetchBlockExplorerParallel<string>(asset, "/tx", {
        method: "POST",
        body: txHex,
    });

    return { id: txId };
};

export const broadcastTransaction = async (
    asset: string,
    txHex: string,
): Promise<{ id: string }> => {
    const results = await Promise.allSettled([
        broadcastApiTransaction(asset, txHex),
        broadcastToExplorer(asset, txHex),
    ]);
    const successfulResult = results.find(
        (result) => result.status === "fulfilled",
    );
    if (successfulResult) {
        return (successfulResult as PromiseFulfilledResult<{ id: string }>)
            .value;
    }

    // When the backend is unavailable its error says nothing about the
    // transaction, so prefer the node rejection an explorer relayed. Callers
    // match on those strings (e.g. "non-final")
    const explorerRejection = getExplorerRejection(
        (results[1] as PromiseRejectedResult).reason,
    );
    if (explorerRejection !== undefined) {
        throw explorerRejection;
    }

    throw (results[0] as PromiseRejectedResult).reason;
};

export const getSwapUTXOs = async (
    swap: ChainSwap | SubmarineSwap,
): Promise<SwapUTXO[]> => {
    const address =
        swap.type === SwapType.Chain
            ? (swap as ChainSwap).lockupDetails.lockupAddress
            : (swap as SubmarineSwap).address;

    const utxos = await getAddressUTXOs(swap.assetSend, address);

    const rawTxs: { hex: string; id: string }[] = [];

    for (const utxo of utxos) {
        const rawTx = await getRawTransaction(swap.assetSend, utxo.txid);
        rawTxs.push({ hex: rawTx, id: utxo.txid });
    }

    return rawTxs.map((rawTx) => {
        if (refundableAssets.includes(swap.assetSend)) {
            return {
                id: rawTx.id,
                hex: rawTx.hex,
                // Important to know if the swap has timed out or not
                timeoutBlockHeight:
                    swap.type === SwapType.Chain
                        ? (swap as ChainSwap).lockupDetails.timeoutBlockHeight
                        : (swap as SubmarineSwap).timeoutBlockHeight,
            };
        }

        return { hex: rawTx.hex, id: rawTx.id };
    });
};

const getEsploraFeeEstimations = async (apiEndpoint: Url) => {
    const { opts, requestTimeout } = constructRequestOptions();
    try {
        const res = await fetch(
            `${chooseUrl(apiEndpoint)}/fee-estimates`,
            opts,
        );

        if (!res.ok) {
            await handleResponseError(res);
        }

        return ((await res.json()) as Record<string, number>)[3];
    } finally {
        clearTimeout(requestTimeout);
    }
};

const getMempoolFeeEstimations = async (mempoolApi: Url) => {
    const { opts, requestTimeout } = constructRequestOptions();
    try {
        const res = await fetch(
            `${chooseUrl(mempoolApi)}/v1/fees/recommended`,
            opts,
        );

        if (!res.ok) {
            await handleResponseError(res);
        }

        return ((await res.json()) as MempoolFeeEstimation).halfHourFee;
    } finally {
        clearTimeout(requestTimeout);
    }
};

export const getFeeEstimations = async (url: ExplorerUrl) => {
    switch (url.id) {
        case Explorer.Mempool:
            return await getMempoolFeeEstimations(url);
        case Explorer.Esplora:
            return await getEsploraFeeEstimations(url);
        default:
            throw new Error(`unknown explorer type: ${url.id}`);
    }
};
